import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import EquipmentListAccordion from "../common/components/LocationSelectionStep/EquipmentListAccordion";
import {
  assetGroupActivated,
  assetGroupDeactivated,
  setSelectedGroup,
} from "../../../redux/slice/bookingCreation/bookingCreationSlice";
import LoaderOverlay from "../../layout/components/LoaderOverlay";
import ListLoadError from "../common/components/ListLoadError";
import { EquipmentListProps } from "../../../types/baseTypes";

function EquipmentSelectionStep() {
  const dispatch = useDispatch();
  const selectedLocation = useSelector(
    (state: RootState) => state.bookingCreation.selectedLocation
  );
  const equipments = useSelector((state: RootState) =>
    selectedLocation
      ? state.bookingCreation.groupsByLocation[selectedLocation.id]
      : undefined
  );
  const isLoading = !equipments?.length;

  const selectedEquipment = useSelector(
    (state: RootState) => state.bookingCreation.selectedGroup
  );
  // `isLoading` above is derived from the list being empty, so a failed load
  // is indistinguishable from a slow one and the spinner never resolves
  // (#1970). The flow records the failure; this is where it becomes visible.
  const loadFailed = useSelector(
    (state: RootState) =>
      !!selectedLocation &&
      state.bookingCreation.groupsLoadFailedLocationId === selectedLocation.id
  );

  const [expandedAccordion, setExpandedAccordion] = useState<string | null>(
    selectedEquipment ? selectedEquipment.id : null
  ); // Track the currently expanded accordion

  // Auto-select the only available equipment so the user gets clear "Selected"
  // feedback and the "Next" button reflects an actual selection. Guarded with a
  // ref so the auto-select runs at most once per location to avoid re-selecting
  // after the user might (in the future) deselect.
  const autoSelectedRef = useRef<string | null>(null);
  // Reset the guard when the location changes so a new 1-item list at a
  // different location can still trigger an auto-select.
  useEffect(() => {
    autoSelectedRef.current = null;
  }, [selectedLocation?.id]);
  useEffect(() => {
    if (!equipments) return;
    const eligible = equipments.filter((e: EquipmentListProps) => e.priceCategoryInfo);
    if (eligible.length !== 1) return;
    const only = eligible[0];
    if (autoSelectedRef.current === only.id) return;
    if (selectedEquipment?.id === only.id) {
      autoSelectedRef.current = only.id;
      return;
    }
    autoSelectedRef.current = only.id;
    dispatch(assetGroupActivated(only));
    dispatch(setSelectedGroup(only));
    setExpandedAccordion(only.id);
  }, [equipments, selectedEquipment, dispatch]);

  const handleAccordionChange = (equipment: EquipmentListProps, expanded: boolean) => {
    setExpandedAccordion(expanded ? equipment.id : null);
    if (expanded) {
      dispatch(assetGroupActivated(equipment));
      dispatch(setSelectedGroup(equipment));
    } else {
      dispatch(assetGroupDeactivated());
    }
  };

  if (loadFailed) {
    return <ListLoadError messageKey="book_location.error.fetch_equipment" />;
  }

  return (
    <>
      <div className="flex flex-col gap-4 mx-4 max-h-[98%] overflow-y-auto">
        {equipments?.map((item: EquipmentListProps) =>
          item.priceCategoryInfo ? (
            <EquipmentListAccordion
              key={item.id}
              expanded={expandedAccordion === item.id}
              onAccordionChange={(expanded) => handleAccordionChange(item, expanded)}
              equipment={item}
              priceCategoryInfo={item.priceCategoryInfo}
              image={item.featuredImage}
              name={item.name}
              langNames={item.langNames}
              selected={selectedEquipment?.id === item.id}
            />
          ) : null
        )}
      </div>
      <LoaderOverlay open={isLoading} />
    </>
  );
}

export default EquipmentSelectionStep;
